const getFullLocation = (activity) =>
    [activity.activityAddress, activity.activityCity, activity.activityCountry]
        .filter(Boolean)
        .join(", ");

const getBaseStartDate = (startDate) => {
    if (!startDate) {
        const fallbackDate = new Date();
        fallbackDate.setDate(fallbackDate.getDate() + 30);
        return fallbackDate;
    }

    const [year, month, day] = startDate.split('-').map(Number);
    return new Date(year, month - 1, day);
};

export const generateICSFile = (activity, dayInDestination, startDate) => {
    try {
        if (!activity || !activity.activityDuration || !activity.activityStartTime) {
            throw new Error("Invalid activity data. Missing required fields.");
        }

        const baseDate = getBaseStartDate(startDate);
        baseDate.setDate(baseDate.getDate() + Number(dayInDestination) - 1);

        const [hours, minutes] = activity.activityStartTime.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) throw new Error("Invalid start time format.");

        const startDateTime = new Date(baseDate);
        startDateTime.setHours(
            activity.activityNoTime === "1" ? hours + 9 : hours,
            minutes, 0, 0
        );

        const [durationHours, durationMinutes] = activity.activityDuration.split(':').map(Number);
        const endDateTime = new Date(startDateTime);
        endDateTime.setHours(endDateTime.getHours() + durationHours);
        endDateTime.setMinutes(endDateTime.getMinutes() + durationMinutes);

        const formatICSDate = (date) => date.toISOString().replace(/-|:|\.\d+/g, "");

        const location = getFullLocation(activity);

        const icsData = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${formatICSDate(startDateTime)}
DTEND:${formatICSDate(endDateTime)}
SUMMARY:${activity.activityName || "Event"}
DESCRIPTION:${activity.activityDescription || ""}
LOCATION:${location}
END:VEVENT
END:VCALENDAR`;

        const blob = new Blob([icsData], { type: "text/calendar" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${activity.activityName || "event"}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

    } catch (error) {
        console.error("Error generating ICS file:", error.message);
    }
};

export const generateGoogleCalendarLink = (activity, dayInDestination, startDate) => {
    try {
        if (!activity || !activity.activityDuration || !activity.activityStartTime) {
            throw new Error("Invalid activity data. Missing required fields.");
        }


        const baseDate = getBaseStartDate(startDate);
        baseDate.setDate(baseDate.getDate() + Number(dayInDestination) - 1);

        const [hours, minutes] = activity.activityStartTime.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) throw new Error("Invalid start time format.");

        const startDateTime = new Date(baseDate);
        startDateTime.setHours(
            activity.activityNoTime === "1" ? hours + 9 : hours,
            minutes, 0, 0
        );

        const [durationHours, durationMinutes] = activity.activityDuration.split(':').map(Number);
        const endDateTime = new Date(startDateTime);
        endDateTime.setHours(endDateTime.getHours() + durationHours);
        endDateTime.setMinutes(endDateTime.getMinutes() + durationMinutes);

        const format = (date) => date.toISOString().replace(/-|:|\.\d+/g, "");
        const location = getFullLocation(activity);

        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(activity.activityName || "Event")}&details=${encodeURIComponent(activity.activityDescription || "")}&location=${encodeURIComponent(location)}&dates=${format(startDateTime)}/${format(endDateTime)}`;
    } catch (error) {
        console.error("Error generating Google Calendar link:", error.message);
        return null;
    }
};

export const generateOutlookCalendarLink = (activity, dayInDestination, startDate) => {
    try {
        if (!activity || !activity.activityDuration || !activity.activityStartTime) {
            throw new Error("Invalid activity data. Missing required fields.");
        }

        const baseDate = getBaseStartDate(startDate);
        baseDate.setDate(baseDate.getDate() + Number(dayInDestination) - 1);

        const [hours, minutes] = activity.activityStartTime.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) throw new Error("Invalid start time format.");

        const startDateTime = new Date(baseDate);
        startDateTime.setHours(
            activity.activityNoTime === "1" ? hours + 9 : hours,
            minutes, 0, 0
        );

        const [durationHours, durationMinutes] = activity.activityDuration.split(':').map(Number);
        const endDateTime = new Date(startDateTime);
        endDateTime.setHours(endDateTime.getHours() + durationHours);
        endDateTime.setMinutes(endDateTime.getMinutes() + durationMinutes);

        const formatOutlookDate = (date) => encodeURIComponent(date.toISOString().slice(0, 19));
        const location = getFullLocation(activity);

        const subject = encodeURIComponent(activity.activityName || "Event");
        const body = encodeURIComponent(
            (activity.activityDescription || "") +
            (activity.activityUrl ? `\n\nMore info: ${activity.activityUrl}` : "")
        );

        const link = `https://outlook.live.com/calendar/0/action/compose?allday=false&startdt=${formatOutlookDate(startDateTime)}&enddt=${formatOutlookDate(endDateTime)}&subject=${subject}&body=${body}&location=${location}&path=/calendar/action/compose&rru=addevent`;

        window.open(link, '_blank');
    } catch (error) {
        console.error("Error generating Outlook Calendar link:", error.message);
        return "";
    }
};
